import React from "react";
import LinkComponent from "../../app/components/link/LinkComponent";
import {AlertComponent} from "../../app/components/alert/AlertComponent";
import {Paths} from "../../app/components/sidebar/Paths";
import {GradientHeaderComponent} from "../../app/components/gradientheader/GradientHeaderComponent";
import {H2SectionComponent, H3SectionComponent} from "../../app/components/section/SectionComponent";
import {ThemeColor} from "../../home/components/homecard/HomeCardComponent";

export default function ITBehavioralScreen() {
    return (<div className="screen">
        <Header/>
        <General/>
        <StarMethod/>
        <LevelDifferential/>
    </div>)
}

function Header() {
    return (<GradientHeaderComponent title={Paths.interviewTips.nested.behavioral.name}
                                     tagline={Paths.interviewTips.nested.behavioral.tagline}/>)
}

function General() {
    return (<H2SectionComponent header="General Tips & Tricks" removeTopPadding={true}>
        <Importance/>
        <DeterminingLevel/>
        <FocusOnYou/>
        <StoryTeller/>
        <DoNotLie/>
        <Prepare/>
        <TechnicalVLeadership/>
    </H2SectionComponent>)
}

function Importance() {
    return (<H3SectionComponent header="Arguably one of the most important rounds for Software Engineers."
                                removeTopPadding={true}>
        <p>Whenever I see someone has been "down-leveled" or rejected, the first thing in my mind is <i>they messed
            up the behavioral part</i>. I am truly surprised at how little emphasis is put on this interview.</p>
    </H3SectionComponent>)
}

function DeterminingLevel() {
    return (<H3SectionComponent header="This round is pivotal in determining which level you will be hired in at.">
        <p>Just like your resume, you want to make sure your answers are showing impact at the level you
            are <i>wanting</i>, not the level you <i>have</i>.</p>
        <p>Looking for entry-level? You better show you are a good human being. Looking for mid-level? Make sure you
            highlight your ability to work with little oversight on your work. Senior? What impact are you having on
            your <i>team</i>? Staff+? What impact are you having on your <i>organization</i>?</p>
        <p>Some companies like Google or Meta will actually hire generally, and tell you the level afterwards. Some
            companies will ask you to apply to a specific level ahead of time, but will reject you if your
            behavioral responses don't meet the criteria they are looking for at that level.</p>
        <p>One thing to keep in mind, be realistic. Remember not lying? Your experience of figuring out how to write
            your first unit test isn't going to get you a Staff-level job. Self-awareness will get you far in life.
            Writing Unit Tests <i>can</i> get you a mid-level job if you frame it the right way. "Early in my career
            I made an update to production and it blocked users from being able to update their password for 24
            hours. While I'm lucky it wasn't a more important feature, it made me realize that tomorrow it could be
            payments. I wanted to find ways to manage production rollouts, so I took it upon myself to learn
            different methods of testing: unit, integration, etc. to prevent issues as much as possible." See, you
            still were learning testing, but now you phrased it in a way that is more self-sufficient, so now I'm
            seeing you in a mid-level light. You proved you can identify mistakes you've made, and think about how
            you can prevent those issues in the future. Et voilà!</p>
        <AlertComponent color={ThemeColor.Secondary}>Just in case it hasn't been clear enough, behavioral interviews are
            critical for determining what level you will be hired in at.</AlertComponent>
    </H3SectionComponent>)
}

function FocusOnYou() {
    return (<H3SectionComponent header="Focus on what YOU did.">
        <p>Engineering is a team sport, it's easy to say "We did this." "My team did that." The company you are
            interviewing for wants to know what <i>you</i> did. What impact did <i>you</i> have. This is
            something that is truly harder for women than it is men. My best advice is to practice, practice,
            practice. Don't take credit for other people's work, but don't feel bad taking full credit for the
            work you did, either.</p>
    </H3SectionComponent>)
}

function StoryTeller() {
    return (<H3SectionComponent header="Be a good story teller.">
        <p>The higher up you go, the more important this is. Interviewers give interviews a <i>lot</i>. Make it
            worth their wild. Make them want to care, make them want to list. Set the stage, mention the conflict,
            share the resolution, give 'em the ol' happy ending.</p>
    </H3SectionComponent>)
}

function DoNotLie() {
    return (<H3SectionComponent header="Doctors hate this one simple trick - do not lie.">
        <p>A lot of the following advice may result in you thinking to yourself, well, I don't have any experience
            for the job I want, I gotta make some good stories up to prove I'm at the level I want to be at. No.
            Baadddddd idea. There is a huge difference between reframing your very-real to be perceived at a
            specific level, and just making up an experience to begin with. Let's say you have a company that really
            wants employees with Jetpack Compose experience. You may think to yourself, "Well, it's not like they
            are asking me to write Jetpack Compose, I'mma just tell them I did a side project - yeah, that'll fool
            them." So you say, "I worked on a side project that uses Jetpack Compose." But they immediately follow
            it up with, "What was the project about?" Uh-oh. Maybe you are quick on your feet, you say "It's a way
            for users to keep up with football scores around the world." Phew, you think. You did it, you got away
            with it. "What was the hardest part about transitioning from XML to Jetpack Compose?" Well, oops. They
            know. They always know. Don't do it. It's tempting, don't do it.</p>
    </H3SectionComponent>)
}

function Prepare() {
    return (<H3SectionComponent header="You cannot overprepare for this round.">
        <p>You probably aren't spending as much time on this as you are LeetCode, but spend some time to really
            think about good examples in your job that are relevant to the position you are looking to get at the
            next company. Make sure you are thinking about the STAR method, and framing those examples
            accordingly.</p>
    </H3SectionComponent>)
}

function TechnicalVLeadership() {
    return (<H3SectionComponent
        header="Have an equal number of technical and leadership examples if you are looking at Senior+ jobs.">
        <p>You are still trying for a <i>technical</i> leadership position. Don't forget the technical. But also,
            you are looking for the leadership side of things too. Be well-balanced, for every "I mentored this
            person" there should be a "I had to walk back this architecture decision here" kind of a thing.</p>
    </H3SectionComponent>)
}

function StarMethod() {
    return (<H2SectionComponent header="STAR Method">
        <p>The STAR method is a great way to quickly and eloquently share stories during
            your behavioral interview. It comprises of four (4) main parts to each story 1) Situation, 2) Task, 3)
            Action, and 4) Result. These four (4) things are so important to cover in answering each question the
            interviewer has for you.</p>
        <p>You will never know the questions ahead of time. So, it's impossible to
            memorize the exact answers to the questions the interviewer will ask you. However, I would still put
            thought into what are some of your biggest accomplishments and challenges you faced. What setbacks have
            you had and what did you learn from it? How do you give feedback, or better yet take feedback in?</p>
        <p>I had a few bullet points in front of me during the interviews - especially the
            virtual ones. I wouldn't recommend reading from them verbatim as it can come across poorly, but just a
            good reminder of some of your best professional moments to keep you on the right track. If you are
            struggling to think of stories on your own, I <i>highly</i> recommend <LinkComponent
                link="https://www.hellointerview.com/tools/story-builder">HelloInterview's Tool</LinkComponent>. This is
            one of the best use cases for AI I have found.</p>
        <Situation/>
        <Task/>
        <Action/>
        <Result/>
    </H2SectionComponent>)
}

function Situation() {
    return (<H3SectionComponent header="Situation" tagline="Give me 1-2 sentences of context.">
        <p>I, personally, found giving a single sentence for what the context is, like...</p>
        <ul>
            <li>I was on a team doing X</li>
            <li>We were currently following Y process</li>
            <li>We just hired Z people within a week</li>
            <li>Team was focused on A</li>
        </ul>
        <p>followed by why that matters for the story you are about to tell...</p>
        <ul>
            <li>Which was leading to us missing deadlines</li>
            <li>Causing code review process to take an average of X days</li>
            <li>Without any experience, but we needed them on client work ASAP</li>
            <li>Which led to us forgetting about B</li>
        </ul>
        <p>is usually a good starting place.</p>
    </H3SectionComponent>)
}

function Task() {
    return (<H3SectionComponent header="Task" tagline="What are you trying to change about that situation?">
        <ul>
            <li>I wanted to identify a way to keep track of timelines better</li>
            <li>As the lead, I needed to reduce the time spent on code review</li>
            <li>I was proactive in putting together training material and clear expectations for new hires</li>
            <li>Recognizing we forgot about our second priority B, I wanted to escalate the issue, and guide teams
                towards a long-term focused architecture that would allow us to more easily focus on A and B.
            </li>
        </ul>
    </H3SectionComponent>)
}

function Action() {
    return (<H3SectionComponent header="Action" tagline="What exact steps did you take to implement said change?">
        <ul>
            <li>I took the lead in defining a roadmap after researching and client engagement. I then came up with a
                better tracking system to keep track of progress towards our new shared goals.
            </li>
            <li>I raised concerns to the team about our long running average of code review time. After analyzing
                our process Y, I identified the following ABC shortfalls. I proposed a solution of Z instead.
            </li>
            <li>I made a quick website with training materials relevant to the job. I associated it with a calendar
                roadmap so they can make sure they were staying on track with expectations.
            </li>
            <li>I escalated the issue to our VPs, resulting in a staff-level engineer being assigned to the project.
                Together, we advocated for and implemented a Clean Architecture approach. I created an example
                feature module for other teams to reference. Etc.
            </li>
        </ul>
    </H3SectionComponent>)
}

function Result() {
    return (<H3SectionComponent header="Result" tagline="What impact did those actions have?">
        <ul>
            <li>App ratings improved from X to Y. Increase client engagement led to an contract extension. Revenue
                increased by Z% due to the new features implemented as a direct result of the roadmap I created.
            </li>
            <li>Code review turnaround time went from X days to Y days. Team morale also went up as a result with
                team satisfaction scores going from A to B.
            </li>
            <li>X out Y people were assigned to client work within Z days. Client-contracts were met as a result of
                fast-tracking their training.
            </li>
            <li>This new architecture allowed us scale the project easier, allowing the team to focus on A and B
                more easily. It also increased communication across different teams as a result.
            </li>
        </ul>
    </H3SectionComponent>)
}

function LevelDifferential() {
    return (<H2SectionComponent header="Level Differential">
        <EntryLevel/>
        <MidLevel/>
        <SeniorLevel/>
        <StaffLevel/>
        <PrincipleLevel/>
    </H2SectionComponent>)
}

function EntryLevel() {
    return (<H3SectionComponent header="Entry-Level (E3 at Google/Meta)" removeTopPadding={true}>
        <ul>
            <li>Are you someone we want to work with?</li>
            <li>Are you someone who is willing to learn?</li>
            <li>Are you good with feedback?</li>
        </ul>
    </H3SectionComponent>)
}

function MidLevel() {
    return (<H3SectionComponent header="Mid-Level (E4 at Google/Meta)">
        <ul>
            <li>Are you self-sufficient?</li>
            <li>Can you own small features by yourself?</li>
            <li>Are you thinking about ways you can grow your craft?</li>
        </ul>
    </H3SectionComponent>)
}

function SeniorLevel() {
    return (<H3SectionComponent header="Senior-Level (E5 at Google/Meta)">
        <ul>
            <li>How are you helping the team?</li>
            <li>Can you own big features / projects by yourself?</li>
            <li>How do you handle the bad stuff?</li>
            <li>How do you impact the good stuff?</li>
            <li>What are you doing to grow your leadership skills?</li>
        </ul>
    </H3SectionComponent>)
}

function StaffLevel() {
    return (<H3SectionComponent header="Staff-Level (E6 at Google/Meta)">
        <ul>
            <li>How are you helping the organization?</li>
            <li>What are you doing to share knowledge across teams?</li>
            <li>What do your office hours look like?</li>
            <li>What are you focused on teaching others about your platform right now?</li>
        </ul>
    </H3SectionComponent>)
}

function PrincipleLevel() {
    return (<H3SectionComponent header="Principle-Level (E7+ at Google/Meta)">
        <ul>
            <li>How are you helping the company?</li>
            <li>What roadmaps have you created to drive progress forward?</li>
            <li>What steps do you take to ensure the team is working towards the company's vision?</li>
        </ul>
    </H3SectionComponent>)
}
